import React, { useState, useEffect } from "react";
import { colors } from '../../theme/colors';
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Avatar from "@mui/material/Avatar";
import { useNavigate } from "react-router-dom";
import { CustomAppBar, StyledBox } from "./style";
import { Divider, IconButton, Tooltip } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useMe } from "../../store/query/userQuery";
import copy from "copy-to-clipboard";
import { useToast } from "../../contexts-hooks/hooks/useToster";
import { useLogOut } from "../../store/query/userAuthenticationQuery";
import useGlobalContext from "../../contexts-hooks/hooks/useGlobalContext";

const Header = ({ open }) => {
  const logOutMutation = useLogOut();
  const [anchorEl, setAnchorEl] = useState(null);
  const [recruiterInfo, setRecruiterInfo] = useState({
    name: null,
    email: null,
  });
  const navigate = useNavigate();
  const isMenuOpen = Boolean(anchorEl);
  const { showToast } = useToast();
  const { state } = useGlobalContext(); 
  const user = state?.user; 

  useEffect(() => {
    if (user) {
      setRecruiterInfo({
        name: user.name || user.firstName || "Recruiter",
        email: user.email || "recruiter@example.com",
      });
    }
  }, [user]);

  const handleCopyEmail = () => {
    if (recruiterInfo.email) {
      copy(recruiterInfo.email);
      showToast("Email copied to clipboard", "success");
    }
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutUser = () => {
    sessionStorage.removeItem("global_talent_search_state");
    sessionStorage.clear();
    logOutMutation.mutate();
    navigate("/");
  };

  const getInitials = (name) => {
    return name ? name.charAt(0).toUpperCase() : null;
  };

  const generateColor = (userId) => {
    if (!userId) return colors.primary;
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      hash = userId.charCodeAt(i) + ((hash << 5) - hash);
    }
    return `hsl(${hash % 360}, 60%, 50%)`;
  };

  const menuId = "primary-search-account-menu";
  const renderMenu = (
    <Menu
      sx={{ mt: "15px" }}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
      PaperProps={{
        elevation: 3,
        sx: {
          overflow: "visible",
          filter: "drop-shadow(0px 8px 24px rgba(0,0,0,0.12))",
          mt: 1.5,
          borderRadius: "12px",
          border: "1px solid rgba(145, 158, 171, 0.12)",
          "& .MuiMenuItem-root": {
            borderRadius: "8px",
            mx: 1,
            my: 0.5,
            px: 1.5,
            py: 1,
            fontSize: "14px",
            fontWeight: 600,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(211, 47, 47, 0.08)",
            },
          },
          "&:before": {
            content: '""',
            display: "block",
            position: "absolute",
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: "background.paper",
            transform: "translateY(-50%) rotate(45deg)",
            zIndex: 0,
            borderLeft: "1px solid rgba(145, 158, 171, 0.12)",
            borderTop: "1px solid rgba(145, 158, 171, 0.12)",
          },
        },
      }}
    >
      <Box sx={{ px: 2, py: 1.5, minWidth: 240 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e293b" }}>
          {recruiterInfo.name
            ? recruiterInfo.name.charAt(0).toUpperCase() +
              recruiterInfo.name.slice(1)
            : ""}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 0.5, gap: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "13px" }}>
            {recruiterInfo.email}
          </Typography>
          <Tooltip title="Copy Email" arrow>
            <IconButton
              onClick={handleCopyEmail}
              size="small"
              sx={{
                color: "text.secondary",
                p: 0.5,
                borderRadius: "6px",
                border: "1px solid #e2e8f0",
                transition: "all 0.2s ease",
                "&:hover": {
                  color: colors.primary,
                  borderColor: colors.primary,
                  backgroundColor: "rgba(127, 29, 29, 0.04)",
                },
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Divider sx={{ my: 0.5 }} />
      <MenuItem onClick={handleLogoutUser} sx={{ color: "error.main" }}>
        <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
        Logout
      </MenuItem>
    </Menu>
  );

  return (
    <StyledBox className="container">
      <CustomAppBar position="static">
        <Toolbar>
          <Box className="flexGrow" />
          <Box className="profileContainer">
            <Box className="profileBox" onClick={handleProfileMenuOpen}>
              <Typography variant="subtitle1" className="profileName">
                {recruiterInfo.name
                  ? recruiterInfo.name.charAt(0).toUpperCase() +
                    recruiterInfo.name.slice(1)
                  : ""}
              </Typography>
              <Avatar
                className="profileAvatar"
                sx={{
                  bgcolor: generateColor(user?._id),
                }}
              >
                {getInitials(recruiterInfo.name)}
              </Avatar>
            </Box>
          </Box>
        </Toolbar>
      </CustomAppBar>
      {renderMenu}
    </StyledBox>
  );
};

export default Header;
